/**
 * MobileAdminSwitch — fixed bottom two-segment toggle that lets admins
 * switch between the Admin Panel (/housekeeping) and their own inbox (/inbox)
 * on mobile. Always visible, never scrolls away. Admin-only; mobile-only.
 *
 * Mounted ONCE globally in App.js. Do NOT render per-screen — that caused
 * layout jumps between views because each screen wrapped the switch in a
 * slightly different container.
 *
 * Self-guards on 3 axes:
 *   1. role: renders only for admin/superadmin
 *   2. viewport: md:hidden (Tailwind hides on >= 768px)
 *   3. path: only on /inbox and /housekeeping (and /housekeeping sub-routes)
 *
 * Returns null silently in any other case.
 */
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Shield, Inbox } from "lucide-react";

export default function MobileAdminSwitch() {
  const { user } = useAuth();
  const location = useLocation();
  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  if (!isAdmin) return null;

  const path = location.pathname;
  const onInbox = path === "/inbox" || path.startsWith("/inbox/");
  const onAdmin = path === "/housekeeping" || path.startsWith("/housekeeping/");
  // Suppress on the admin login page — user isn't authenticated there yet.
  const onAdminLogin = path === "/housekeeping/login";
  if (!(onInbox || onAdmin) || onAdminLogin) return null;

  const active = onAdmin ? "admin" : "inbox";

  const segCls = (isActive) =>
    `flex-1 flex items-center justify-center gap-1.5 h-full text-xs font-medium transition-colors ${
      isActive
        ? "bg-zinc-900 text-white"
        : "bg-white text-[#555555] hover:text-[#111111] active:bg-[#F5F5F5]"
    }`;

  return (
    <nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-30 h-14 border-t border-[#E5E5E5] bg-white flex pb-safe"
      data-testid="mobile-admin-switch"
      role="navigation"
      aria-label="Admin context switch"
    >
      <NavLink
        to="/housekeeping"
        className={segCls(active === "admin")}
        data-testid="mobile-admin-switch-admin"
        aria-current={active === "admin" ? "page" : undefined}
      >
        <Shield size={15} strokeWidth={1.75} />
        Admin Panel
      </NavLink>
      <div className="w-px bg-[#E5E5E5]" aria-hidden />
      <NavLink
        to="/inbox"
        className={segCls(active === "inbox")}
        data-testid="mobile-admin-switch-inbox"
        aria-current={active === "inbox" ? "page" : undefined}
        onClick={() => {
          // Already on an /inbox route? React Router won't remount, so
          // notify InboxPage to close the email-detail overlay (and reset
          // any internal state it wants to clear). Harmless otherwise.
          if (onInbox) {
            window.dispatchEvent(new CustomEvent("quickinbox:inbox-home"));
          }
        }}
      >
        <Inbox size={15} strokeWidth={1.75} />
        My Inbox
      </NavLink>
    </nav>
  );
}
